import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageCircle, Send, Inbox, Mail, Loader2, Trash2, Reply, ArrowLeft } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const MessagesPage = () => {
  const { user, api } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposeDialog, setShowComposeDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('inbox');
  const [newMessage, setNewMessage] = useState({
    recipient_id: '',
    recipient_username: '',
    subject: '',
    content: ''
  });
  const [searchingUser, setSearchingUser] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);

  // Thread view state
  const [threadOpen, setThreadOpen] = useState(false);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadOtherUser, setThreadOtherUser] = useState(null);
  const [threadReply, setThreadReply] = useState('');
  const [threadSubject, setThreadSubject] = useState('');
  const threadEndRef = useRef(null);

  // Auto-open compose when navigated with ?to=userId
  useEffect(() => {
    const toUserId = searchParams.get('to');
    if (!toUserId || !user) return;

    const prefillRecipient = async () => {
      try {
        const res = await api.get(`/users/${toUserId}`);
        const recipient = res.data;
        setNewMessage({
          recipient_id: recipient.id,
          recipient_username: recipient.display_name || recipient.username,
          subject: '',
          content: ''
        });
        setShowComposeDialog(true);
        setSearchParams({}, { replace: true });
      } catch {
        toast.error('Could not find that member.');
      }
    };
    prefillRecipient();
  }, [searchParams, user, api, setSearchParams]);

  const fetchMessages = useCallback(async () => {
    try {
      const [inboxRes, sentRes] = await Promise.allSettled([
        api.get('/messages/inbox'),
        api.get('/messages/sent')
      ]);
      if (inboxRes.status === 'fulfilled') setInbox(inboxRes.value.data);
      if (sentRes.status === 'fulfilled') setSent(sentRes.value.data);
    } catch {
      console.error('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const openThread = async (message, isSent) => {
    const otherUserId = isSent ? message.recipient_id : message.sender_id;
    const otherUserName = isSent ? message.recipient_name : message.sender_name;

    setThreadOtherUser({ id: otherUserId, name: otherUserName });
    setThreadSubject(message.subject.startsWith('Re: ') ? message.subject : `Re: ${message.subject}`);
    setThreadOpen(true);
    setThreadLoading(true);

    try {
      const res = await api.get(`/messages/thread/${otherUserId}`);
      setThreadMessages(res.data);
      // Mark inbox messages from this user as read locally
      setInbox(prev => prev.map(m =>
        m.sender_id === otherUserId ? { ...m, is_read: true } : m
      ));
    } catch {
      toast.error('Could not load conversation.');
      setThreadOpen(false);
    } finally {
      setThreadLoading(false);
    }
  };

  // Scroll to bottom when thread loads or new messages arrive
  useEffect(() => {
    if (threadEndRef.current && threadMessages.length > 0) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [threadMessages]);

  const handleSendThreadReply = async (e) => {
    e.preventDefault();
    if (!threadReply.trim() || !threadOtherUser) return;

    setSending(true);
    try {
      const res = await api.post('/messages', {
        recipient_id: threadOtherUser.id,
        subject: threadSubject,
        content: threadReply
      });
      setThreadMessages(prev => [...prev, res.data]);
      setSent(prev => [res.data, ...prev]);
      setThreadReply('');
      toast.success('Reply sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const handleSearchUser = async (query) => {
    if (query.length < 1) {
      setUserSearchResults([]);
      return;
    }
    setSearchingUser(true);
    try {
      const res = await api.get(`/users/mention-search?q=${encodeURIComponent(query)}&limit=10`);
      setUserSearchResults(res.data.filter(u => u.id !== user.id));
    } catch {
      console.error('User search failed');
    } finally {
      setSearchingUser(false);
    }
  };

  const handleSelectRecipient = (selectedUser) => {
    setNewMessage(prev => ({
      ...prev,
      recipient_id: selectedUser.id,
      recipient_username: selectedUser.display_name || selectedUser.username
    }));
    setUserSearchResults([]);
  };

  const resetComposeForm = () => {
    setNewMessage({ recipient_id: '', recipient_username: '', subject: '', content: '' });
    setUserSearchResults([]);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.recipient_id || !newMessage.subject.trim() || !newMessage.content.trim()) {
      toast.error('Please fill in all fields and select a recipient.');
      return;
    }

    setSending(true);
    try {
      const res = await api.post('/messages', {
        recipient_id: newMessage.recipient_id,
        subject: newMessage.subject,
        content: newMessage.content
      });
      setSent(prev => [res.data, ...prev]);
      resetComposeForm();
      setShowComposeDialog(false);
      setActiveTab('sent');
      toast.success(`Message sent to ${res.data.recipient_name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleReply = (message) => {
    setNewMessage({
      recipient_id: message.sender_id,
      recipient_username: message.sender_name,
      subject: message.subject.startsWith('Re: ') ? message.subject : `Re: ${message.subject}`,
      content: ''
    });
    setShowComposeDialog(true);
  };

  const handleDeleteMessage = async (messageId, isSent = false) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/messages/${messageId}`);
      if (isSent) {
        setSent(prev => prev.filter(m => m.id !== messageId));
      } else {
        setInbox(prev => prev.filter(m => m.id !== messageId));
      }
      setThreadMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted.');
    } catch {
      toast.error('Failed to delete message.');
    }
  };

  const MessageCard = ({ message, isSent = false }) => (
    <Card 
      className={`bg-white border-brand-primary/10 cursor-pointer card-hover ${!message.is_read && !isSent ? 'border-l-4 border-l-brand-accent' : ''}`}
      onClick={() => openThread(message, isSent)}
      data-testid={isSent ? `sent-message-${message.id}` : `inbox-message-${message.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-brand-primary text-white">
              {(isSent ? message.recipient_name : message.sender_name)?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-text-primary">
                {isSent ? `To: ${message.recipient_name}` : message.sender_name}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-text-muted">
                  {new Date(message.created_at).toLocaleDateString()}
                </span>
                {!isSent && (
                  <button
                    data-testid={`reply-message-card-${message.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReply(message);
                    }}
                    className="p-1 rounded-md text-text-muted hover:text-brand-accent hover:bg-brand-accent/10 transition-colors"
                    title="Reply"
                  >
                    <Reply className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteMessage(message.id, isSent);
                  }}
                  className="p-1 rounded-md text-text-muted hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="Delete message"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className={`text-sm ${!message.is_read && !isSent ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}>
              {message.subject}
            </p>
            <p className="text-xs text-text-muted line-clamp-1 mt-1">
              {message.content}
            </p>
          </div>
          {!message.is_read && !isSent && (
            <Badge className="bg-brand-accent/10 text-brand-accent">New</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const unreadCount = inbox.filter(m => !m.is_read).length;

  // Thread view
  if (threadOpen) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="thread-view">
        {/* Thread Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => { setThreadOpen(false); fetchMessages(); }}
            data-testid="back-to-inbox"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-brand-primary text-white">
                {threadOtherUser?.name?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="font-serif text-xl font-semibold text-text-primary">
                {threadOtherUser?.name}
              </h1>
              <p className="text-xs text-text-muted">
                {threadMessages.length} message{threadMessages.length !== 1 ? 's' : ''} in conversation
              </p>
            </div>
          </div>
        </div>

        {/* Thread Messages */}
        <div className="bg-white rounded-xl border border-brand-primary/10 mb-4">
          {threadLoading ? (
            <div className="p-8 space-y-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-lg" />)}
            </div>
          ) : threadMessages.length === 0 ? (
            <div className="p-12 text-center">
              <MessageCircle className="w-12 h-12 text-text-muted mx-auto mb-3" />
              <p className="text-text-secondary">No messages in this conversation yet.</p>
            </div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto p-4 space-y-4" data-testid="thread-messages">
              {threadMessages.map((msg) => {
                const isMe = msg.sender_id === user.id;
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                    data-testid={`thread-msg-${msg.id}`}
                  >
                    <div className={`max-w-[75%] ${isMe ? 'order-1' : ''}`}>
                      <div
                        className={`rounded-2xl px-4 py-3 ${
                          isMe
                            ? 'bg-brand-primary text-white rounded-br-md'
                            : 'bg-surface-alt text-text-primary rounded-bl-md'
                        }`}
                      >
                        <p className={`text-xs font-medium mb-1 ${isMe ? 'text-white/70' : 'text-text-muted'}`}>
                          {msg.subject}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      <div className={`flex items-center gap-2 mt-1 ${isMe ? 'justify-end' : ''}`}>
                        <span className="text-[11px] text-text-muted">
                          {isMe ? 'You' : msg.sender_name} &bull; {new Date(msg.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <button
                          onClick={() => handleDeleteMessage(msg.id, isMe)}
                          className="p-0.5 rounded text-text-muted/50 hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={threadEndRef} />
            </div>
          )}

          {/* Reply form at bottom of thread */}
          <form onSubmit={handleSendThreadReply} className="border-t border-brand-primary/10 p-4" data-testid="thread-reply-form">
            <div className="flex gap-3">
              <Textarea
                placeholder={`Reply to ${threadOtherUser?.name}...`}
                value={threadReply}
                onChange={(e) => setThreadReply(e.target.value)}
                rows={2}
                className="flex-1 resize-none"
                data-testid="thread-reply-input"
              />
              <Button
                type="submit"
                className="btn-accent self-end gap-2"
                disabled={sending || !threadReply.trim()}
                data-testid="thread-send-button"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="messages-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-text-primary mb-2">Messages</h1>
          <p className="text-text-secondary">
            Connect with fellow writers
            {unreadCount > 0 && <span className="ml-2 text-brand-accent">({unreadCount} unread)</span>}
          </p>
        </div>
        <Dialog open={showComposeDialog} onOpenChange={(open) => {
          setShowComposeDialog(open);
          if (!open) resetComposeForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-accent gap-2" data-testid="compose-button">
              <Mail className="w-4 h-4" />
              Compose
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" aria-describedby="compose-dialog-desc">
            <DialogHeader>
              <DialogTitle>New Message</DialogTitle>
              <p id="compose-dialog-desc" className="sr-only">Compose and send a private message to another member</p>
            </DialogHeader>
            <form onSubmit={handleSendMessage} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>To</Label>
                {newMessage.recipient_id ? (
                  <div className="flex items-center justify-between p-2 bg-brand-primary/5 rounded-lg">
                    <span className="font-medium">{newMessage.recipient_username}</span>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setNewMessage(prev => ({ ...prev, recipient_id: '', recipient_username: '' }))}
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      placeholder="Type a name to find members..."
                      onChange={(e) => handleSearchUser(e.target.value)}
                      data-testid="recipient-search"
                    />
                    {searchingUser && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
                      </div>
                    )}
                    {userSearchResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-brand-primary/10 rounded-lg shadow-soft-md z-10 max-h-60 overflow-y-auto" data-testid="recipient-dropdown">
                        {userSearchResults.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            className="w-full flex items-center gap-3 p-3 hover:bg-brand-primary/5 transition-colors text-left"
                            onClick={() => handleSelectRecipient(u)}
                            data-testid={`recipient-option-${u.id}`}
                          >
                            <Avatar className="h-8 w-8 shrink-0">
                              <AvatarImage src={u.avatar_url} />
                              <AvatarFallback className="bg-brand-primary/10 text-brand-primary text-xs">
                                {(u.display_name || u.username)?.[0]?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-text-primary truncate">{u.display_name || u.username}</p>
                              <p className="text-xs text-text-muted">@{u.username}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder="Message subject"
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage(prev => ({ ...prev, subject: e.target.value }))}
                  required
                  data-testid="message-subject"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Message</Label>
                <Textarea
                  id="content"
                  placeholder="Write your message..."
                  value={newMessage.content}
                  onChange={(e) => setNewMessage(prev => ({ ...prev, content: e.target.value }))}
                  required
                  rows={5}
                  data-testid="message-content"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowComposeDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="btn-primary gap-2" 
                  disabled={sending || !newMessage.recipient_id}
                  data-testid="send-message-button"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-surface-alt mb-6">
          <TabsTrigger value="inbox" className="gap-2" data-testid="tab-inbox">
            <Inbox className="w-4 h-4" />
            Inbox
            {unreadCount > 0 && (
              <Badge className="bg-brand-accent text-white ml-1">{unreadCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sent" className="gap-2" data-testid="tab-sent">
            <Send className="w-4 h-4" />
            Sent
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
            </div>
          ) : inbox.length === 0 ? (
            <div className="text-center py-20">
              <MessageCircle className="w-16 h-16 text-text-muted mx-auto mb-4" />
              <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">
                No messages yet
              </h2>
              <p className="text-text-secondary">Your inbox is empty. Start a conversation!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {inbox.map((message) => (
                <MessageCard key={message.id} message={message} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="sent">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
            </div>
          ) : sent.length === 0 ? (
            <div className="text-center py-20">
              <Send className="w-16 h-16 text-text-muted mx-auto mb-4" />
              <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">
                No sent messages
              </h2>
              <p className="text-text-secondary">You haven't sent any messages yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sent.map((message) => (
                <MessageCard key={message.id} message={message} isSent />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MessagesPage;
